import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Svg, { Circle } from 'react-native-svg';

import { scaleSize } from '~/constants/scale';
import { useStyles } from '~/hooks';
import { Theme } from '~/theme';

type Props = {
    percent?: number | null;
    size?: number;
    strokeWidth?: number;
    color?: string;
    trackColor?: string;
    // Center label override; defaults to "{percent}%" (or "…" when indeterminate).
    label?: string;
};

const clampPercent = (value?: number | null): number | null => {
    if (value === undefined || value === null || !Number.isFinite(value)) {
        return null;
    }
    return Math.max(0, Math.min(100, value));
};

export default function CircularProgress({ percent, size, strokeWidth, color, trackColor, label }: Props) {
    const styles = useStyles(stylesGenerator);
    const dim = size ?? scaleSize(180);
    const stroke = strokeWidth ?? scaleSize(12);
    const ringColor = color ?? (styles.ring.color as string);
    const ringTrack = trackColor ?? (styles.track.color as string);

    const radius = (dim - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const pct = clampPercent(percent);
    // Indeterminate: show a quarter arc as a hint; determinate: fill to pct.
    const fill = pct === null ? 0.25 : pct / 100;
    const dashoffset = circumference * (1 - fill);
    const center = dim / 2;
    const centerText = label ?? (pct === null ? '…' : `${Math.round(pct)}%`);

    return (
        <View style={[styles.container, { width: dim, height: dim }]}>
            <Svg width={dim} height={dim}>
                <Circle cx={center} cy={center} r={radius} stroke={ringTrack} strokeWidth={stroke} fill="none" />
                <Circle
                    cx={center}
                    cy={center}
                    r={radius}
                    stroke={ringColor}
                    strokeWidth={stroke}
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${circumference} ${circumference}`}
                    strokeDashoffset={dashoffset}
                    // Start the arc at 12 o'clock.
                    transform={`rotate(-90 ${center} ${center})`}
                />
            </Svg>
            <View style={styles.labelWrap}>
                <Text style={[styles.labelText, { fontSize: Math.round(dim * 0.22) }]}>{centerText}</Text>
            </View>
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: {
            alignItems: 'center',
            justifyContent: 'center',
        },
        labelWrap: {
            ...StyleSheet.absoluteFillObject,
            alignItems: 'center',
            justifyContent: 'center',
        },
        labelText: {
            color: theme.text,
            fontWeight: '300',
        },
        // Color holders read by the SVG (it needs raw color strings).
        ring: { color: theme.button },
        track: { color: theme.textSecondary },
    });
